import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import { setAllRecipesData } from "../homepage/homepage.slice";
import { IngredientsTableInterface } from "../homepage/homepage.types";
import { DEFAULT_FILTERS } from "./search.constants";
import { getServiceFiltersSearchList, getServiceQueryIngredientsList, getServiceQuerySearchList } from "./search.thunk";
import { SearchFilteredRecipesInterface, SearchFiltersInterface, SearchHit, SearchIngredients, SearchState } from "./search.types";

const initialState: SearchState = {
  searchString: '',
  searchQueryLoading: false,
  searchFilteredRecipes: [],
  searchFilters: DEFAULT_FILTERS,
  ingredientsNameList: [],
  cuisineNameList: [],
  mealTypeList: [],
  searchPageFilteredRecipes: [],
  sortMode: "recommended",
  searchFilteredIngredients: [],
  defaultExplorePageSearchList: [],
};

export const search = createSlice({
  name: "search",
  initialState,
  reducers: {
    setSearchString: (state, action: PayloadAction<string>) => {
      state.searchString = action.payload;
    },
    setSearchFilteredRecipes: (state, action: PayloadAction<{ localFilteredRecipes: Array<SearchHit>, queryString: string }>) => {
      state.searchFilteredRecipes = action.payload.localFilteredRecipes;
      state.searchString = action.payload.queryString;
    },
    setSearchFilteredIngredients: (state, action: PayloadAction<{ localFilteredIngredients: Array<SearchIngredients>, queryString: string }>) => {
      state.searchFilteredIngredients = action.payload.localFilteredIngredients;
    },
    setSearchFilters: (state, action: PayloadAction<SearchFiltersInterface>) => {
      state.searchFilters = action.payload;
    },
    setCuisineFilter: (state, action: PayloadAction<Array<string>>) => {
      state.searchFilters.cuisineFilter = action.payload;
    },
    setIngredientFilter: (state, action: PayloadAction<Array<string>>) => {
      state.searchFilters.ingredientFilter = action.payload;
    },
    setCourseFilter: (state, action: PayloadAction<string>) => {
      state.searchFilters.courseFilter = action.payload;
    },
    setTimeFilter: (state, action: PayloadAction<string>) => {
      state.searchFilters.timeFilter = action.payload;
    },
    setServingSizeFilter: (state, action: PayloadAction<Array<string>>) => {
      state.searchFilters.servingSizeFilter = action.payload;
    },
    setMealTypeFilter: (state, action: PayloadAction<Array<string>>) => {
      state.searchFilters.mealTypeFilter = action.payload;
    },
    setSearchQueryLocalFilteredRecipes: (state, action: PayloadAction<{ localFilteredRecipes: Array<SearchHit>, queryString: string }>) => {
      state.searchFilteredRecipes = action.payload.localFilteredRecipes;
      state.searchString = action.payload.queryString;
    },
    setSearchLocalFilterRecipes: (state, action: PayloadAction<{ localFilteredRecipes: Array<SearchFilteredRecipesInterface>, filteredRecipes: Array<SearchFilteredRecipesInterface> }>) => {
      state.searchFilteredRecipes = action.payload.localFilteredRecipes;
      state.searchPageFilteredRecipes = action.payload.filteredRecipes;
    },
    setClearFilters: (state, action: PayloadAction<{ localFilteredRecipes: Array<SearchHit> }>) => {
      state.searchFilters = DEFAULT_FILTERS;
      state.sortMode = "recommended";
      state.searchFilteredRecipes = action.payload.localFilteredRecipes;
    },
    setSortMode: (state, action: PayloadAction<string>) => {
      state.sortMode = action.payload;
    },
    setSortedRecipes: (state, action: PayloadAction<{ sortedRecipes: Array<SearchFilteredRecipesInterface> }>) => {
      state.searchFilteredRecipes = action.payload.sortedRecipes;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getServiceQuerySearchList.fulfilled, (state, action: PayloadAction<{ algoliaResults: Array<SearchHit> }>) => {
        state.searchFilteredRecipes = action.payload.algoliaResults;
        state.defaultExplorePageSearchList = action.payload.algoliaResults;
        state.searchQueryLoading = false;
      })
      .addCase(getServiceQuerySearchList.pending, (state) => {
        state.searchQueryLoading = true;
      })
      .addCase(getServiceQuerySearchList.rejected, (state) => {
        state.searchQueryLoading = false;
      })
      .addCase(getServiceQueryIngredientsList.fulfilled, (state, action: PayloadAction<{ algoliaResults: Array<SearchIngredients> }>) => {
        state.searchFilteredIngredients = action.payload.algoliaResults;
        // state.ingredientQueryLoading = false
      })
      .addCase(getServiceFiltersSearchList.fulfilled, (state, action: PayloadAction<{ algoliaResults: Array<SearchHit>, filteredRecipes: Array<SearchFilteredRecipesInterface> }>) => {
        state.searchFilteredRecipes = action.payload.algoliaResults;
        state.searchPageFilteredRecipes = action.payload.filteredRecipes;
        state.searchQueryLoading = false;
      })
      .addCase(getServiceFiltersSearchList.rejected, (state) => {
        state.searchQueryLoading = false;
      })
      .addCase(getServiceFiltersSearchList.pending, (state) => {
        state.searchQueryLoading = false;
      })
      .addCase(setAllRecipesData, (state, action: PayloadAction<{
        cuisinesList: Array<string>;
        ingredientsData: Array<IngredientsTableInterface>;
        ingredientsList: Array<string>;
        mealTypeList: Array<string>;
        recipeMetaData: Array<SearchFilteredRecipesInterface>;
      }>) => {
        state.ingredientsNameList = action.payload.ingredientsList;
        state.cuisineNameList = action.payload.cuisinesList;
        state.mealTypeList = action.payload.mealTypeList;
      });
  }
});

// Action creators are generated for each case reducer function
export const {
  setSearchString,
  setSearchFilteredRecipes,
  setSearchFilters,
  setCuisineFilter,
  setIngredientFilter,
  setCourseFilter,
  setTimeFilter,
  setServingSizeFilter,
  setMealTypeFilter,
  setSearchQueryLocalFilteredRecipes,
  setSearchLocalFilterRecipes,
  setClearFilters,
  setSortMode,
  setSortedRecipes,
  setSearchFilteredIngredients
} = search.actions;

export default search.reducer;
